import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import FolderSidebar from '../components/dashboard/FolderSidebar';
import AlertList from '../components/dashboard/AlertList';
import MobileDrawer from '../components/dashboard/MobileDrawer';
import TermsModal from '../components/dashboard/TermsModal';
import NotificationQueue from '../components/dashboard/NotificationQueue';
import { showAlertNotification, requestNotificationPermission, showBrowserNotification } from '../components/dashboard/NotificationToast';
import { useRealtimeAlerts, useTermsVersionMonitor } from '../components/realtime/useRealtimeAlerts';
import { Button } from "../components/ui/button";
import { RefreshCw, Bell, Loader2, Wifi, WifiOff, FolderOpen } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from '../context/AuthContext';
import * as userApi from '../services/userApi';

// Reuse the exact same dashboard component logic for the Admin View
export default function AdminIndicatorAlerts() {
    const { user, loading: authLoading } = useAuth();
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [userLoading, setUserLoading] = useState(true);
    const [termsAccepting, setTermsAccepting] = useState(false);
    const [termsStatus, setTermsStatus] = useState(null);
    const queryClient = useQueryClient();

    // Get current user and terms status
    useEffect(() => {
        const loadUser = async () => {
            if (authLoading) return;

            if (user) {
                setUserLoading(true);
                try {
                    const status = await userApi.getTermsStatus(user.id);
                    setTermsStatus(status);
                    requestNotificationPermission();
                } catch (error) {
                    console.error("Failed to load user data/terms", error);
                } finally {
                    setUserLoading(false);
                }
            } else {
                setUserLoading(false);
            }
        };
        loadUser();
    }, [user, authLoading]);

    const hasAcceptedTerms = termsStatus?.accepted === true;

    // Accept terms mutation
    const acceptTerms = async () => {
        setTermsAccepting(true);
        await userApi.acceptTerms(user.id, termsStatus.terms_version_id);
        setTermsStatus({ ...termsStatus, accepted: true });
        setTermsAccepting(false);
    };

    // Handle terms version update - force re-acceptance
    const handleTermsUpdated = useCallback(async () => {
        const newStatus = await userApi.getTermsStatus(user.id);
        setTermsStatus(newStatus);
    }, [user?.id]);

    // Monitor for new terms versions
    useTermsVersionMonitor({
        userId: user?.id,
        currentTermsVersionId: termsStatus?.terms_version_id,
        onTermsUpdated: handleTermsUpdated
    });

    // Fetch folders with follow status
    const { data: folders = [], isLoading: foldersLoading } = useQuery({
        queryKey: ['userFolders', user?.id],
        queryFn: () => userApi.getUserFolders(user.id),
        enabled: !!(user?.id && hasAcceptedTerms),
    });

    // Check if user follows a folder
    const isFollowingFolder = (folderId) => {
        const folder = folders.find(f => f.id === folderId);
        return folder?.is_enabled === true;
    };

    // Fetch alerts for selected folder
    const { data: alerts = [], isLoading: alertsLoading, refetch: refetchAlerts } = useQuery({
        queryKey: ['userAlerts', selectedFolder?.id],
        queryFn: () => userApi.getUserAlerts(user.id, selectedFolder?.id, 100),
        enabled: !!(user?.id && selectedFolder?.id && hasAcceptedTerms && (isFollowingFolder(selectedFolder.id) || user?.role === 'admin')),
    });

    // Fetch alert counts for all folders
    const { data: allAlerts = [] } = useQuery({
        queryKey: ['allAlerts'],
        queryFn: () => userApi.getAllAlertsForCounting(),
        enabled: !!hasAcceptedTerms,
    });

    // Fetch notifications for user
    const { data: notifications = [], isLoading: notificationsLoading } = useQuery({
        queryKey: ['userNotifications', user?.id],
        queryFn: () => userApi.getUserNotifications(user.id, 100),
        enabled: !!(user?.id && hasAcceptedTerms),
    });

    const unreadCount = notifications.filter(n => !n.is_read).length;

    // Calculate alert counts per folder
    const alertCounts = allAlerts.reduce((acc, alert) => {
        acc[alert.folder_id] = (acc[alert.folder_id] || 0) + 1;
        return acc;
    }, {});

    // Toggle folder follow mutation with Optimistic Update
    const toggleFollow = useMutation({
        mutationFn: async (folderId) => {
            const folder = folders.find(f => f.id === folderId);
            const newStatus = !folder?.is_enabled;
            return userApi.toggleFolderFollow(user.id, folderId, newStatus);
        },
        onMutate: async (folderId) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['userFolders', user?.id] });

            // Snapshot the previous value
            const previousFolders = queryClient.getQueryData(['userFolders', user?.id]);

            // Optimistically update to the new value
            queryClient.setQueryData(['userFolders', user?.id], (old) => {
                return old.map(folder =>
                    folder.id === folderId
                        ? { ...folder, is_enabled: !folder.is_enabled }
                        : folder
                );
            });

            // Return a context object with the snapshotted value
            return { previousFolders };
        },
        onError: (err, folderId, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            queryClient.setQueryData(['userFolders', user?.id], context.previousFolders);
            console.error("Failed to toggle folder:", err);
            // Optional: Show error toast
        },
        onSettled: () => {
            // Always refetch after error or success to ensure data sync
            queryClient.invalidateQueries({ queryKey: ['userFolders'] });
        },
    });

    // Handle clicking on a notification - navigate to the alert
    const handleNotificationClick = useCallback((notification) => {
        const alert = allAlerts.find(a => a.id === notification.alert_id);
        if (alert) {
            const folder = folders.find(f => f.id === alert.folder_id);
            if (folder) {
                setSelectedFolder(folder);
            }
        }
    }, [folders, allAlerts]);

    // Handle clicking on toast - navigate to the alert
    const handleToastClick = useCallback((alert) => {
        const folder = folders.find(f => f.id === alert.folder_id);
        if (folder) {
            setSelectedFolder(folder);
        }
    }, [folders]);

    // Real-time alert handler
    const handleNewAlert = useCallback((alert) => {
        showAlertNotification(alert, handleToastClick);
        showBrowserNotification(alert);
    }, [handleToastClick]);

    // Real-time notification handler
    const handleNewNotification = useCallback((notification) => {
        // Notification queue updates automatically via query invalidation
    }, []);

    // Setup real-time subscriptions
    const { isConnected } = useRealtimeAlerts({
        userId: user?.id,
        hasAcceptedTerms,
        folders,
        onNewAlert: handleNewAlert,
        onNewNotification: handleNewNotification
    });



    const visibleAlerts = selectedFolder && isFollowingFolder(selectedFolder.id)
        ? alerts
        : [];

    // Check if user has any enabled folders
    const hasEnabledFolders = folders.some(f => f.is_enabled === true);

    // Show loading state while checking user
    if (authLoading || userLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-slate-400">Loading...</p>
                </div>
            </div>
        );
    }

    // Show terms modal if not accepted
    if (!hasAcceptedTerms) {
        return (
            <div className="h-full bg-slate-900">
                <TermsModal
                    open={true}
                    onAccept={acceptTerms}
                    isLoading={termsAccepting}
                    termsContent={termsStatus?.terms_content}
                />
            </div>
        );
    }

    return (
        <div className="h-full bg-slate-900 flex overflow-hidden">
            <Toaster richColors position="top-right" />

            {/* Desktop Sidebar (Sub-sidebar for Folders) */}
            <div className="hidden lg:block w-80 shrink-0 border-r border-slate-800">
                <FolderSidebar
                    folders={folders}
                    alertCounts={alertCounts}
                    selectedFolder={selectedFolder}
                    onSelectFolder={setSelectedFolder}
                    onToggleFollow={(id) => toggleFollow.mutate(id)}
                    isLoading={foldersLoading}
                />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="h-16 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 lg:px-6 shrink-0">
                    <div className="flex items-center gap-3">
                        <MobileDrawer
                            folders={folders}
                            alertCounts={alertCounts}
                            selectedFolder={selectedFolder}
                            onSelectFolder={setSelectedFolder}
                            onToggleFollow={(id) => toggleFollow.mutate(id)}
                            isLoading={foldersLoading}
                            open={mobileOpen}
                            onOpenChange={setMobileOpen}
                        />
                        <div>
                            <h1 className="text-lg font-semibold text-white">
                                {selectedFolder ? selectedFolder.name : 'Trading Alerts'}
                            </h1>
                            {selectedFolder && (
                                <p className="text-xs text-slate-500">
                                    {isFollowingFolder(selectedFolder.id)
                                        ? `${visibleAlerts.length} alerts • Notifications ON`
                                        : 'Folder is OFF • Turn on to see alerts'
                                    }
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Connection status indicator */}
                        <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-800/50">
                            {isConnected ? (
                                <>
                                    <Wifi className="h-3 w-3 text-emerald-400" />
                                    <span className="text-xs text-emerald-400">Live</span>
                                </>
                            ) : (
                                <>
                                    <WifiOff className="h-3 w-3 text-slate-500" />
                                    <span className="text-xs text-slate-500">Offline</span>
                                </>
                            )}
                        </div>

                        {/* Note: Admin Access Button removed here since we are ALREADY in admin */}

                        <NotificationQueue
                            notifications={notifications}
                            unreadCount={unreadCount}
                            onNotificationClick={handleNotificationClick}
                            isLoading={notificationsLoading}
                            userId={user?.id}
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => refetchAlerts()}
                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </header>

                {/* Alert Content */}
                <main className="flex-1 overflow-y-auto">
                    {!hasEnabledFolders ? (
                        <div className="flex flex-col items-center justify-center h-full text-slate-500 p-6">
                            <div className="w-20 h-20 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4">
                                <FolderOpen className="w-10 h-10 text-slate-600" />
                            </div>
                            <p className="text-lg font-medium text-slate-400">No signal systems enabled</p>
                            <p className="text-sm text-slate-600 mt-1 text-center max-w-sm">
                                Please enable a system from the sidebar to receive alerts
                            </p>
                        </div>
                    ) : selectedFolder && !isFollowingFolder(selectedFolder.id) ? (
                        <div className="flex flex-col items-center justify-center h-full text-slate-500 p-6">
                            <div className="w-20 h-20 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4">
                                <Bell className="w-10 h-10 text-slate-600" />
                            </div>
                            <p className="text-lg font-medium text-slate-400">Folder is turned OFF</p>
                            <p className="text-sm text-slate-600 mt-1 text-center max-w-sm">
                                Turn on the folder toggle in the sidebar to view alerts and receive notifications
                            </p>
                        </div>
                    ) : (
                        <AlertList
                            alerts={visibleAlerts}
                            isLoading={alertsLoading}
                            selectedFolder={selectedFolder}
                        />
                    )}
                </main>
            </div>
        </div>
    );
}
